import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    IconButton,
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio
} from '@mui/material';
import CustomDatePicker from '../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import PreviewIcon from '@mui/icons-material/Preview';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import QueueIcon from '@mui/icons-material/Queue';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Swal from 'sweetalert2';
import EnumsService from '../../Services/enums.service.js';
import DocumentService from '../../Services/documents.service.js';
import bankCheckImage from '../../assests/images/bank_check_page.png';
import AddBankModal from '../BankMatching/AddBankModal.js';
import Constants from '../../assests/Constants/constants.js';
import { amountToHebrewWords } from '../../Utils/hebrewAmount.js';
import SignaturePad from 'react-signature-canvas';
import fieldStyles from '../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import nestedStyles from './PrintChecksModal.module.scss';

const createQueueItemId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const mapPaymentRowToQueueItem = (row, checkNumber, reprint = false) => {
    const firstCheck = row.checks?.[0];
    const grossAmount = firstCheck?.amount ?? row.amount ?? 0;
    // When reprinting keep the check's original reference/number instead of assigning a new one.
    // Printed checks store their number on the PaymentAcceptance's `reference` field
    // (set to request.CheckNumber on print), so fall back to it as the source of truth.
    const originalCheckNumber =
        firstCheck?.reference2 ||
        firstCheck?.checkNumber ||
        row.reference2 ||
        row.checkNumber ||
        row.reference;

    return {
        id: createQueueItemId(),
        payTo: row.accountCodeName || row.description || firstCheck?.description || '',
        amountInNumbers: grossAmount,
        amountInWords: '',
        dueDate: firstCheck?.paymentDate
            ? new Date(firstCheck.paymentDate)
            : (row.date ? new Date(row.date) : new Date()),
        details: row.description || firstCheck?.description || '',
        supplierAccountId: row.accountCodeId || firstCheck?.debitAccount || '',
        withholdingTax: row.withholdingTax || 0,
        checkNumber: (reprint && originalCheckNumber ? originalCheckNumber : checkNumber).toString(),
    };
};

const CheckQueueThumbnail = ({ item }) => {
    const finalAmount = item.amountInNumbers - (item.withholdingTax || 0);

    return (
        <Box
            sx={{
                width: 76,
                height: 48,
                borderRadius: '6px',
                overflow: 'hidden',
                position: 'relative',
                flexShrink: 0,
                border: '1px solid #cfd8ff',
                boxShadow: '0 1px 3px rgba(48, 79, 255, 0.1)',
                backgroundColor: '#fff',
            }}
        >
            <Box
                component="img"
                src={bankCheckImage}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.88) 65%, #ffffff 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    px: 0.5,
                    pb: 0.35,
                }}
            >
                <Typography sx={{ fontSize: '6px', fontWeight: 700, color: '#304FFF', lineHeight: 1.1 }} noWrap>
                    #{item.checkNumber}
                </Typography>
                <Typography sx={{ fontSize: '7px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.1 }} noWrap>
                    ₪{finalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
            </Box>
        </Box>
    );
};

const PrintedCheckPreviewCard = ({ item, index, t, onClick }) => {
    const withholding = Number(item.withholdingTax) || 0;
    const finalAmount = Number(item.amountInNumbers);
    const dueDate = item.dueDate ? format(new Date(item.dueDate), 'dd/MM/yyyy') : '';

    return (
        <Paper
            elevation={0}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
            sx={{
                width: '100%',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #dde3ff',
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(48, 79, 255, 0.06)',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.15s ease',
                '&:hover': onClick ? {
                    borderColor: '#304FFF',
                    boxShadow: '0 4px 16px rgba(48, 79, 255, 0.14)',
                    transform: 'translateY(-1px)',
                } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', direction: 'rtl' }}>
                <Box sx={{ width: 4, flexShrink: 0, bgcolor: '#304FFF' }} />

                <Box sx={{ flex: 1, p: 1.25, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch' }}>
                        <Box sx={{
                            width: 108,
                            flexShrink: 0,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #cfd8ff',
                            bgcolor: '#f8f9ff',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <Box sx={{ flex: 1, position: 'relative', minHeight: 62 }}>
                                <Box
                                    component="img"
                                    src={bankCheckImage}
                                    alt=""
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        display: 'block',
                                        p: 0.25,
                                    }}
                                />
                            </Box>
                            <Box sx={{
                                bgcolor: '#304FFF',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 700,
                                textAlign: 'center',
                                py: 0.35,
                                px: 0.5,
                            }}>
                                {t('CheckNumber')} {item.checkNumber}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Chip
                                    size="small"
                                    label={`${index + 1}`}
                                    sx={{
                                        height: 20,
                                        minWidth: 24,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        bgcolor: '#eef1ff',
                                        color: '#304FFF',
                                    }}
                                />
                                <Typography sx={{ fontSize: '11px', color: '#888', flexShrink: 0 }}>
                                    {dueDate}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    color: '#1a1a1a',
                                    lineHeight: 1.25,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.payTo}
                            </Typography>

                            <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#304FFF', lineHeight: 1.2 }}>
                                ₪{finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>

                            <Typography sx={{
                                fontSize: '11px',
                                color: '#555',
                                lineHeight: 1.35,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}>
                                {item.amountInWords}
                            </Typography>

                            {item.details && (
                                <Typography sx={{
                                    fontSize: '10px',
                                    color: '#999',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {item.details}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

const CheckImagePreviewPanel = ({ item, signatureSrc, includeNoChangesText, t }) => {
    const finalAmount = Number(item.amountInNumbers);
    const dueDate = item.dueDate ? format(new Date(item.dueDate), 'dd/MM/yyyy') : '';

    return (
        <Box sx={{ direction: 'rtl' }}>
            <Box sx={{
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #cfd8ff',
                bgcolor: '#fafbff',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.8)',
            }}>
                <Box
                    component="img"
                    src={bankCheckImage}
                    alt={t('CheckImage')}
                    sx={{
                        width: '100%',
                        display: 'block',
                        objectFit: 'contain',
                        bgcolor: 'white',
                    }}
                />
            </Box>

            <Paper elevation={0} sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: '10px',
                border: '2px solid #304FFF',
                bgcolor: 'white',
                position: 'relative',
                overflow: 'hidden',
            }}>
                <Typography sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#304FFF',
                    mb: 1,
                    textAlign: 'center',
                }}>
                    {t('CheckFillPreview')}
                </Typography>

                <Box sx={{
                    position: 'relative',
                    border: '1px dashed #ccc',
                    borderRadius: '6px',
                    minHeight: 120,
                    p: 1.5,
                    bgcolor: '#fff',
                    fontFamily: 'Arial, Helvetica, sans-serif',
                }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, textAlign: 'right', mb: 1, borderBottom: '1px solid #000', pb: 0.5 }}>
                        {item.payTo}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', textAlign: 'right', mb: 0.75, borderBottom: '1px solid #000', pb: 0.5, lineHeight: 1.35 }}>
                        {item.amountInWords}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#304FFF' }}>
                            ₪{finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, borderBottom: '1px solid #000', pb: 0.25 }}>
                            {dueDate}
                        </Typography>
                    </Box>
                    {signatureSrc && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Box component="img" src={signatureSrc} alt="" sx={{ maxHeight: 36, maxWidth: 100, objectFit: 'contain' }} />
                        </Box>
                    )}
                    {includeNoChangesText && (
                        <Typography sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            fontSize: '8px',
                            fontWeight: 700,
                            border: '1px solid #333',
                            px: 0.5,
                            py: 0.15,
                        }}>
                            בשיק זה אין לבצע שינוי
                        </Typography>
                    )}
                    <Typography sx={{
                        position: 'absolute',
                        bottom: 6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '9px',
                        color: '#888',
                        fontWeight: 600,
                    }}>
                        {t('CheckNumber')}: {item.checkNumber}
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};

const PrintedCheckDetailView = ({ item, index, signatureSrc, includeNoChangesText, t }) => {
    const [viewMode, setViewMode] = useState('details');
    const withholding = Number(item.withholdingTax) || 0;
    const finalAmount = Number(item.amountInNumbers);
    const grossAmount = finalAmount + withholding;
    const dueDate = item.dueDate ? format(new Date(item.dueDate), 'dd/MM/yyyy') : '';

    const detailRow = (label, value) => (
        <Box sx={{ py: 1, borderBottom: '1px solid #eef1ff' }}>
            <Typography sx={{ fontSize: '11px', color: '#888', mb: 0.35 }}>{label}</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#222', lineHeight: 1.4 }}>{value || '—'}</Typography>
        </Box>
    );

    return (
        <Box sx={{ direction: 'rtl' }}>
            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, value) => value && setViewMode(value)}
                size="small"
                fullWidth
                sx={{
                    mb: 1.5,
                    bgcolor: '#eef1ff',
                    borderRadius: '10px',
                    p: 0.5,
                    '& .MuiToggleButton-root': {
                        flex: 1,
                        border: 'none',
                        borderRadius: '8px !important',
                        textTransform: 'none',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#666',
                        gap: 0.5,
                        '&.Mui-selected': {
                            bgcolor: 'white',
                            color: '#304FFF',
                            boxShadow: '0 1px 4px rgba(48,79,255,0.15)',
                        },
                    },
                }}
            >
                <ToggleButton value="details">
                    <ListAltIcon sx={{ fontSize: 16 }} />
                    {t('ShowCheckDetails')}
                </ToggleButton>
                <ToggleButton value="image">
                    <ImageOutlinedIcon sx={{ fontSize: 16 }} />
                    {t('ShowCheckImage')}
                </ToggleButton>
            </ToggleButtonGroup>

            {viewMode === 'image' ? (
                <CheckImagePreviewPanel
                    item={item}
                    signatureSrc={signatureSrc}
                    includeNoChangesText={includeNoChangesText}
                    t={t}
                />
            ) : (
                <>
                    <Box sx={{
                        position: 'relative',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: '1px solid #dde3ff',
                        bgcolor: '#f8f9ff',
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                    }}>
                        <Box sx={{
                            width: 72,
                            height: 48,
                            borderRadius: '6px',
                            overflow: 'hidden',
                            border: '1px solid #cfd8ff',
                            flexShrink: 0,
                        }}>
                            <Box component="img" src={bankCheckImage} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700 }} noWrap>{item.payTo}</Typography>
                            <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#304FFF' }}>
                                ₪{finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                        <Chip size="small" label={`#${index + 1}`} sx={{ bgcolor: '#304FFF', color: 'white', fontWeight: 700 }} />
                    </Box>

                    <Paper elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8ebff', px: 2, py: 0.5 }}>
                        {detailRow(t('CheckNumber'), item.checkNumber)}
                        {detailRow(t('PayTo'), item.payTo)}
                        {detailRow(t('DuePaymentDate'), dueDate)}
                        {withholding > 0 && detailRow(t('WithholdingTax'), `₪${withholding.toFixed(2)}`)}
                        {detailRow(t('Amount'), `₪${grossAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                        {detailRow(t('FinalCheckAmount'), `₪${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                        {detailRow(t('AmountInWordsHebrew'), item.amountInWords)}
                        {item.details && detailRow(t('Details'), item.details)}
                    </Paper>
                </>
            )}
        </Box>
    );
};

// Styled components for custom styles

const PrintChecksModal = ({ open, onClose, caseId, selectedChecks = [], token, client, onPrintSuccess, fetchEnums, palCodes, reprintMode = false }) => {
    const { i18n, t } = useTranslation();
    const [direction, setDirection] = useState('rtl');

    const [formData, setFormData] = useState({
        payTo: '',
        amountInNumbers: 0,
        amountInWords: '',
        dueDate: new Date(), // תאריך פירעון
        uploadDate: new Date(), // תאריך הפקה
        checkRestriction: null,
        isTransferable: true,
        maxValidityDate: null,
        maxAmount: null,
        magneticLineData: '0',
        supplierAccountId: '',
        supplierAccountName: '',
        supplierAccountCaseId: '',
        supplierAccountEmail: '',
        supplierAccountPhoneNumber: '',
        supplierAccountLocation: '',
        addPayment: true,
        tBankAccountNumber: '',
        details: '',
        withholdingTax: 0
    });

    const [loading, setLoading] = useState(false);
    const [accountCardOptions, setAccountCardOptions] = useState([]);
    const [addNewSupplierAccountDocument, setAddNewSupplierAccountDocument] = useState(false);
    const [cardIdOption, setCardIdOption] = useState(-1);
    const [showAddProviderModal, setShowAddProviderModal] = useState(false);
    const [tBanksData, setTBanksData] = useState([]);
    const [selectedTBank, setSelectedTBank] = useState(null);
    const [lastCheckNumber, setLastCheckNumber] = useState(0);
    const [originalPayToName, setOriginalPayToName] = useState('');
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(''); // Backend HTML used only for printing
    const [printedChecksPreview, setPrintedChecksPreview] = useState([]);
    const [previewPrintSettings, setPreviewPrintSettings] = useState({
        signatureSrc: '',
        includeNoChangesText: true,
    });
    const [checkDetailView, setCheckDetailView] = useState(null);
    const [isDataSaved, setIsDataSaved] = useState(false);

    // Signature related state
    const [includeSignature, setIncludeSignature] = useState(false); // Whether to include signature at all
    const [signatureOption, setSignatureOption] = useState('existing'); // 'existing' or 'new'
    const [signatureData, setSignatureData] = useState('');
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const sigPad = useRef();

    const [supplierDeductionRate, setSupplierDeductionRate] = useState(null);
    const [useSupplierDeductionRate, setUseSupplierDeductionRate] = useState(false);

    // Check restrictions state
    const [includeNoChangesText, setIncludeNoChangesText] = useState(true); // Whether to include "No changes allowed" text
    const [includePillHeaders, setIncludePillHeaders] = useState(true); // Whether to include pill headers (פרטים / סכום)
    const [newProviderData, setNewProviderData] = useState({
        name: '',
        caseId: '',
        email: '',
        phoneNumber: '',
        location: ''
    });

    // State variables for AddBankModal
    const [createBankModal, setCreateBankModal] = useState(false);
    const [selectedBankForCreate, setSelectedBankForCreate] = useState(null);
    const [bankBranch, setBankBranch] = useState("");
    const [bankAccountNumber, setBankAccountNumber] = useState("");
    const [cardForNewBank, setCardForNewBank] = useState(null);
    const [checkQueue, setCheckQueue] = useState([]);
    const [quickAddCount, setQuickAddCount] = useState(2);
    const [initialSelectionLoaded, setInitialSelectionLoaded] = useState(false);

    // Check restriction options
    const restrictionOptions = [
        { value: 1, label: 'קרוס בלבד' },
        { value: 2, label: 'ללא סחיר' },
        { value: 3, label: 'למוטב בלבד' }
    ];

    useEffect(() => {
        // Update direction based on the current language
        if (i18n.language === 'he' || i18n.language === 'ar') {
            setDirection('ltr');
        } else {
            setDirection('rtl');
        }
    }, [i18n.language]);

    // Fetch account cards and TBanks when modal opens
    useEffect(() => {
        if (open && client && token) {
            fetchAccountCards();
            fetchTBanks();
            fetchLastCheckNumber();
        }
    }, [open, client, token]);

    const fetchAccountCards = async () => {
        try {
            const accountsResponse = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                const accountCardOptions = [{ name: t('addProvider'), value1: 'AddItem', isEditable: true }, ...accountsData];
                setAccountCardOptions(accountCardOptions);

                // Set next card ID - find the biggest number plus one
                if (accountsData && accountsData.length > 0) {
                    const numericCardIds = accountsData
                        .map((option) => parseInt(option.cardId, 10))
                        .filter((id) => !isNaN(id));

                    if (numericCardIds.length > 0) {
                        const maxCardId = Math.max(...numericCardIds);
                        setCardIdOption(maxCardId + 1);
                    } else {
                        setCardIdOption(1);
                    }
                } else {
                    setCardIdOption(1);
                }
            }
        } catch (error) {
            console.error('Error fetching account cards:', error);
            toast.error(t('ErrorFetchingAccountCards'));
        }
    };


    const fetchTBanks = async () => {
        try {
            const tBanks = await EnumsService.GetAllTBanksByCaseId(client.caseId, token);
            setTBanksData(tBanks);
        } catch (error) {
            console.error('Error fetching TBanks:', error);
            toast.error(t('ErrorFetchingTBanks'));
        }
    };

    const fetchLastCheckNumber = async () => {
        try {
            const response = await DocumentService.GetLastCheckNumber(client.caseId, token);
            if (response.ok) {
                const data = await response.json();
                setLastCheckNumber(data.lastCheckNumber || 0);
                // In reprint mode we must preserve the check's ORIGINAL number (set from the
                // selected row). This fetch runs asynchronously on open and would otherwise
                // resolve AFTER the reprint effect and overwrite the original number with the
                // next sequential one (e.g. showing 11 instead of the real 10).
                if (!reprintMode) {
                    // Update the magnetic line data with the next check number
                    const nextCheckNumber = (data.lastCheckNumber || 0) + 1;
                    setFormData(prev => ({
                        ...prev,
                        magneticLineData: nextCheckNumber.toString()
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching last check number:', error);
            // Don't show error toast for this, just use default value
        }
    };

    const normalizeCardId = (id) => (id === null || id === undefined ? '' : String(id));

    const findAccountCardByOptionValue = (cards, optionValue) => {
        const key = normalizeCardId(optionValue);
        return cards.find((c) => normalizeCardId(c.cardId ?? c.CardId) === key);
    };

    /** Positive deduction rate (percent) from account card, or null if missing / invalid / non-positive */
    const getDeductionRatePercentFromCard = (accountCard) => {
        if (!accountCard) return null;
        const raw = accountCard.deductionRate ?? accountCard.DeductionRate;
        if (raw === null || raw === undefined || raw === '') return null;
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
        if (!Number.isFinite(n) || n <= 0) return null;
        return n;
    };

    const calculateWithholdingFromRate = (amount, deductionRate) => {
        const amt = Number(amount);
        const rate = Number(deductionRate);
        if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(amt) || amt <= 0) {
            return 0;
        }
        return Number(((amt * rate) / 100).toFixed(2));
    };

    const handleSupplierAccountChange = (selectedOption) => {
        if (selectedOption?.value1 === 'AddItem') {
            // Show the add provider modal
            setNewProviderData({
                name: '',
                caseId: '',
                email: '',
                phoneNumber: '',
                location: ''
            });
            setSupplierDeductionRate(null);
            setUseSupplierDeductionRate(false);
            setShowAddProviderModal(true);
            return;
        }

        if (!selectedOption) {
            setSupplierDeductionRate(null);
            setUseSupplierDeductionRate(false);
            setFormData((prev) => ({
                ...prev,
                supplierAccountId: '',
                supplierAccountName: '',
                supplierAccountCaseId: '',
                supplierAccountEmail: '',
                supplierAccountPhoneNumber: '',
                supplierAccountLocation: ''
            }));
            setOriginalPayToName('');
            return;
        } else {

            // Set formData fields based on selected option
            const accountCard = findAccountCardByOptionValue(accountCardOptions, selectedOption?.value);

            const normalizedDeductionRate = getDeductionRatePercentFromCard(accountCard);
            const shouldAutoWithhold = normalizedDeductionRate !== null;

            setSupplierDeductionRate(normalizedDeductionRate);
            setUseSupplierDeductionRate(shouldAutoWithhold);

            setFormData((prev) => ({
                ...prev,
                supplierAccountId:
                    selectedOption?.value !== null && selectedOption?.value !== undefined
                        ? String(selectedOption.value)
                        : '',
                supplierAccountName: accountCard ? accountCard.name : '',
                supplierAccountCaseId:
                    (accountCard && accountCard.accountCaseId) || selectedOption?.accountCaseId || '',
                supplierAccountEmail: accountCard ? accountCard.email : '',
                supplierAccountPhoneNumber: accountCard ? accountCard.phoneNumber : '',
                supplierAccountLocation: accountCard ? accountCard.location : '',
                payTo: accountCard ? accountCard.name : prev.payTo,
                ...(shouldAutoWithhold
                    ? {
                        withholdingTax: calculateWithholdingFromRate(
                            Number(prev.amountInNumbers) || 0,
                            normalizedDeductionRate
                        )
                    }
                    : {})
            }));

            // Set the original pay-to name for change detection
            setOriginalPayToName(accountCard ? accountCard.name : '');
        }
    };

    const handleCloseAddNewSupplierAccountDocument = () => {
        setFormData((prev) => ({
            ...prev,
            supplierAccountId: '',
            supplierAccountName: '',
            supplierAccountCaseId: '',
            supplierAccountEmail: '',
            supplierAccountPhoneNumber: '',
            supplierAccountLocation: ''
        }));
        setAddNewSupplierAccountDocument(false);
        setSupplierDeductionRate(null);
        setUseSupplierDeductionRate(false);
    };

    const handleAddProvider = async () => {
        if (!newProviderData.name.trim()) {
            toast.error(t('ProviderNameRequired'));
            return;
        }

        try {
            const response = await EnumsService.AddAccountCard(
                client.caseId,
                cardIdOption.toString(),
                newProviderData.name,
                newProviderData.caseId,
                "ספקים",
                token,
                newProviderData.phoneNumber,
                newProviderData.location,
                newProviderData.email
            );

            if (response.ok) {
                toast.success(t('ProviderAddedSuccessfully'));
                setShowAddProviderModal(false);

                // Refresh account cards list
                await fetchAccountCards();

                // Set the newly created provider as selected
                setFormData((prev) => ({
                    ...prev,
                    supplierAccountId: cardIdOption.toString(),
                    supplierAccountName: newProviderData.name,
                    supplierAccountCaseId: newProviderData.caseId,
                    supplierAccountEmail: newProviderData.email,
                    supplierAccountPhoneNumber: newProviderData.phoneNumber,
                    supplierAccountLocation: newProviderData.location,
                    payTo: newProviderData.name
                }));

                // Set the original pay-to name for change detection
                setOriginalPayToName(newProviderData.name);
                setSupplierDeductionRate(null);
                setUseSupplierDeductionRate(false);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || t('ErrorAddingProvider'));
            }
        } catch (error) {
            console.error('Error adding provider:', error);
            toast.error(t('ErrorAddingProvider'));
        }
    };

    const handleCloseAddProviderModal = () => {
        setShowAddProviderModal(false);
        setNewProviderData({
            name: '',
            caseId: '',
            email: '',
            phoneNumber: '',
            location: ''
        });
    };

    const handleTBankChange = (selectedOption) => {
        if (selectedOption?.isAddOption) {
            // Handle "Add Bank" option
            handleOpenCreateModal();
            return;
        }

        setSelectedTBank(selectedOption);
        setFormData(prev => ({
            ...prev,
            tBankAccountNumber: selectedOption?.accountNumber || ''
        }));
    };

    const formattedAccountCardOptions = accountCardOptions
        .filter(option => option.isEditable) // ✅ filter first
        .map(option => ({
            value: option.cardId,
            label: option.accountCaseId
                ? `${option.name} - ${option.accountCaseId}`
                : `${option.name}`,
            name: option.name,
            accountCaseId: option.accountCaseId || '',
            cardId: option.cardId,
            value1: option.value1,
            kind: option.value1 === 'AddItem' ? 'add' : 'option',
        }));

    const formattedTBankOptions = [
        {
            value: 'ADD_BANK',
            label: t('AddBank'),
            isAddOption: true,
            kind: 'add',
        },
        ...tBanksData.map(bank => ({
            value: bank.accountNumber,
            label: `${bank.bankName} - ${bank.accountNumber}`,
            accountNumber: bank.accountNumber,
            bankName: bank.bankName,
            bankCode: bank.bankCode,
            branchNumber: bank.branchNumber,
            cardId: bank.cardId
        }))
    ];

    const comboInputProps = { disableUnderline: true };
    const getFieldClassName = (hasError = false) =>
        [fieldStyles.styleInput, hasError ? fieldStyles.styleInputRequired : '']
            .filter(Boolean)
            .join(' ');
    const getAutocompleteFieldClassName = (hasError = false) =>
        [nestedStyles.autocompleteField, hasError ? nestedStyles.autocompleteFieldError : '']
            .filter(Boolean)
            .join(' ');
    const supplierMissing = !formData.supplierAccountId;
    const amountMissing = !(Number(formData.amountInNumbers) > 0);
    const bankAccountMissing = !selectedTBank && !formData.tBankAccountNumber;
    const scrollbarSx = {
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#d8daf2',
            borderRadius: 'var(--xtram-radius-pill, 999px)',
        },
    };
    const datePickerInputSx = {
        border: '0 !important',
        borderRadius: '0 !important',
        background: 'transparent !important',
        height: '40px',
        minHeight: '40px',
        boxSizing: 'border-box',
        boxShadow: 'none !important',
        outline: 'none !important',
        padding: '0 8px',
        '&:hover, &:focus, &.Mui-focused, &:focus-within': {
            border: '0 !important',
            boxShadow: 'none !important',
        },
    };
    const filterDirection = i18n.dir() === 'rtl' ? 'rtl' : 'ltr';
    const fieldTextAlign = filterDirection === 'rtl' ? 'right' : 'left';
    const restrictionFilterOptions = [
        { value: '', label: t('None') },
        ...restrictionOptions,
    ];

    // Soft Xtram / EditModal field chrome
    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '40px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
            '& .MuiInputBase-input': {
                direction: direction,
                textAlign: direction === 'ltr' ? 'left' : 'right',
            },
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction !== 'ltr' ? 'left' : 'right',
            direction: direction,
        },
    };

    // Convert number to Hebrew words using the utility and add "בלבד"
    const convertToHebrewWords = (amount) => {
        const hebrewAmount = amountToHebrewWords(amount);
        return hebrewAmount + ' בלבד';
    };

    const grossForSupplierWithholding = Number(formData.amountInNumbers) || 0;
    const supplierCalculatedWithholding =
        supplierDeductionRate !== null
            ? calculateWithholdingFromRate(grossForSupplierWithholding, supplierDeductionRate)
            : 0;
    /** Caption + display: prefer stored withholding when auto mode (handles numeric quirks); fallback to formula */
    const supplierWithholdingDisplayAmount =
        useSupplierDeductionRate && supplierDeductionRate !== null
            ? Number(formData.withholdingTax) || supplierCalculatedWithholding
            : supplierCalculatedWithholding;
    // Initialize queue from selected payment rows when modal opens
    useEffect(() => {
        if (!open) {
            setInitialSelectionLoaded(false);
            setCheckQueue([]);
            return;
        }

        if (open && selectedChecks.length > 0 && !initialSelectionLoaded) {
            const resolvedRows = selectedChecks.filter(
                (row) => row && typeof row === 'object' && (row.id || row.amount || row.accountCodeName)
            );

            if (resolvedRows.length > 0) {
                const baseCheckNumber = (lastCheckNumber || 0) + 1;
                const items = resolvedRows.map((row, idx) =>
                    mapPaymentRowToQueueItem(row, baseCheckNumber + idx, reprintMode)
                );
                setInitialSelectionLoaded(true);

                if (reprintMode) {
                    // Reprint = the payment/check already exists, so reuse its own data and
                    // don't create a new payment document.
                    const firstRow = resolvedRows[0];
                    const firstCheck = firstRow?.checks?.[0];
                    const sourceAccountNumber =
                        firstCheck?.supplierAccountNumber ||
                        firstRow?.tBankAccountNumber ||
                        '';

                    if (items.length === 1) {
                        // A single check: load it straight into the form (Supplier/Amount/etc.)
                        // so it's ready to print directly. Keep the queue empty to avoid
                        // printing the same check twice.
                        const single = items[0];
                        setCheckQueue([]);
                        setFormData((prev) => ({
                            ...prev,
                            addPayment: false,
                            payTo: single.payTo,
                            amountInNumbers: single.amountInNumbers,
                            amountInWords: single.amountInWords || '',
                            dueDate: single.dueDate,
                            details: single.details,
                            supplierAccountId: single.supplierAccountId,
                            withholdingTax: single.withholdingTax || 0,
                            magneticLineData: single.checkNumber,
                            tBankAccountNumber: sourceAccountNumber
                                ? String(sourceAccountNumber)
                                : prev.tBankAccountNumber,
                        }));
                    } else {
                        // Multiple checks: keep them in the queue and just pre-fill the
                        // shared source bank account so the user can print immediately.
                        setCheckQueue(items);
                        setFormData((prev) => ({
                            ...prev,
                            addPayment: false,
                            tBankAccountNumber: sourceAccountNumber
                                ? String(sourceAccountNumber)
                                : prev.tBankAccountNumber,
                        }));
                    }
                } else {
                    setCheckQueue(items);
                }
            }
        }
    }, [open, selectedChecks, lastCheckNumber, initialSelectionLoaded, reprintMode]);

    // In reprint mode, select the matching T-bank option for display once banks are loaded
    useEffect(() => {
        if (!open || !reprintMode || selectedTBank || !formData.tBankAccountNumber) return;
        if (!tBanksData || tBanksData.length === 0) return;

        const match = tBanksData.find(
            (bank) => String(bank.accountNumber) === String(formData.tBankAccountNumber)
        );
        if (match) {
            setSelectedTBank({
                value: match.accountNumber,
                label: `${match.bankName} - ${match.accountNumber}`,
                accountNumber: match.accountNumber,
                bankName: match.bankName,
                bankCode: match.bankCode,
                branchNumber: match.branchNumber,
                cardId: match.cardId,
            });
        }
    }, [open, reprintMode, tBanksData, formData.tBankAccountNumber, selectedTBank]);

    // Save ALL form data to localStorage
    const saveFormDataToLocalStorage = () => {
        const dataToSave = {
            // Main form data
            ...formData,
            // Convert dates to ISO strings for storage
            dueDate: formData.dueDate.toISOString(),
            uploadDate: formData.uploadDate.toISOString(),
            maxValidityDate: formData.maxValidityDate?.toISOString() || null,

            // Additional state variables
            selectedTBank: selectedTBank,
            originalPayToName: originalPayToName,
            tBanksData: tBanksData,
            lastCheckNumber: lastCheckNumber,

            // Bank creation modal data
            selectedBankForCreate: selectedBankForCreate,
            bankBranch: bankBranch,
            bankAccountNumber: bankAccountNumber,
            cardForNewBank: cardForNewBank,
            createBankModal: createBankModal,

            // Provider modal data
            newProviderData: newProviderData,
            showAddProviderModal: showAddProviderModal,
            cardIdOption: cardIdOption,
            addNewSupplierAccountDocument: addNewSupplierAccountDocument,

            // Account card options
            accountCardOptions: accountCardOptions,

            // Signature data
            includeSignature: includeSignature,
            signatureOption: signatureOption,
            signatureData: signatureData,

            // Check restrictions
            includeNoChangesText: includeNoChangesText,
            includePillHeaders: includePillHeaders,

            // System data
            timestamp: new Date().toISOString(),
            caseId: caseId,
            client: client,

            supplierDeductionRate,
            useSupplierDeductionRate
        };

        localStorage.setItem('printCheckFormData', JSON.stringify(dataToSave));
        localStorage.setItem('printCheckFormSaved', 'true');
        setIsDataSaved(true);

        // Reset the indicator after 2 seconds
        setTimeout(() => setIsDataSaved(false), 2000);
    };

    // Load ALL form data from localStorage
    const loadFormDataFromLocalStorage = () => {
        try {
            const savedData = localStorage.getItem('printCheckFormData');
            const isSaved = localStorage.getItem('printCheckFormSaved');

            if (savedData && isSaved === 'true') {
                const parsedData = JSON.parse(savedData);

                // Check if the saved data is for the same case
                if (parsedData.caseId === caseId) {
                    return {
                        // Main form data with date conversion
                        formData: {
                            ...parsedData,
                            // Convert ISO strings back to Date objects
                            dueDate: new Date(parsedData.dueDate),
                            uploadDate: new Date(parsedData.uploadDate),
                            maxValidityDate: parsedData.maxValidityDate ? new Date(parsedData.maxValidityDate) : null
                        },
                        // Additional state variables
                        selectedTBank: parsedData.selectedTBank,
                        originalPayToName: parsedData.originalPayToName,
                        tBanksData: parsedData.tBanksData,
                        lastCheckNumber: parsedData.lastCheckNumber,

                        // Bank creation modal data
                        selectedBankForCreate: parsedData.selectedBankForCreate,
                        bankBranch: parsedData.bankBranch,
                        bankAccountNumber: parsedData.bankAccountNumber,
                        cardForNewBank: parsedData.cardForNewBank,
                        createBankModal: parsedData.createBankModal,

                        // Provider modal data
                        newProviderData: parsedData.newProviderData,
                        showAddProviderModal: parsedData.showAddProviderModal,
                        cardIdOption: parsedData.cardIdOption,
                        addNewSupplierAccountDocument: parsedData.addNewSupplierAccountDocument,

                        // Account card options
                        accountCardOptions: parsedData.accountCardOptions,

                        // Signature data
                        includeSignature: parsedData.includeSignature || false,
                        signatureOption: parsedData.signatureOption || 'existing',
                        signatureData: parsedData.signatureData || '',

                        // Check restrictions
                        includeNoChangesText: parsedData.includeNoChangesText !== undefined ? parsedData.includeNoChangesText : true,
                        includePillHeaders: parsedData.includePillHeaders !== undefined ? parsedData.includePillHeaders : true,

                        supplierDeductionRate: parsedData.supplierDeductionRate ?? null,
                        useSupplierDeductionRate: parsedData.useSupplierDeductionRate ?? false
                    };
                }
            }
        } catch (error) {
            console.error('Error loading form data from localStorage:', error);
        }
        return null;
    };

    // Clear saved form data
    const clearSavedFormData = () => {
        localStorage.removeItem('printCheckFormData');
        localStorage.removeItem('printCheckFormSaved');
    };

    // Auto-save ALL form data every 15 seconds when modal is open
    useEffect(() => {
        if (open) {
            const interval = setInterval(saveFormDataToLocalStorage, 15000); // Save every 15 seconds
            return () => clearInterval(interval);
        }
    }, [open, formData, selectedTBank, tBanksData, lastCheckNumber, caseId, includeSignature, signatureOption, signatureData, includeNoChangesText, includePillHeaders]);

    // Load saved data when modal opens
    useEffect(() => {
        if (open) {
            const savedData = loadFormDataFromLocalStorage();
            if (savedData) {
                // Show confirmation dialog to restore saved data
                Swal.fire({
                    title: t('RestoreSavedData'),
                    text: t('RestoreSavedDataMessage'),
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('No'),
                    confirmButtonColor: '#304FFF',
                    cancelButtonColor: '#6c757d'
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Restore main form data
                        setFormData(savedData.formData);

                        // Restore all additional state variables
                        setSelectedTBank(savedData.selectedTBank);
                        setOriginalPayToName(savedData.originalPayToName);
                        setTBanksData(savedData.tBanksData || []);
                        setLastCheckNumber(savedData.lastCheckNumber || 0);

                        // Restore bank creation modal data
                        setSelectedBankForCreate(savedData.selectedBankForCreate);
                        setBankBranch(savedData.bankBranch || "");
                        setBankAccountNumber(savedData.bankAccountNumber || "");
                        setCardForNewBank(savedData.cardForNewBank);
                        setCreateBankModal(savedData.createBankModal || false);

                        // Restore provider modal data
                        setNewProviderData(savedData.newProviderData || {
                            name: '',
                            caseId: '',
                            email: '',
                            phoneNumber: '',
                            location: ''
                        });
                        setShowAddProviderModal(savedData.showAddProviderModal || false);
                        setCardIdOption(savedData.cardIdOption || -1);
                        setAddNewSupplierAccountDocument(savedData.addNewSupplierAccountDocument || false);

                        // Restore account card options
                        setAccountCardOptions(savedData.accountCardOptions || []);

                        // Restore signature data
                        setIncludeSignature(savedData.includeSignature || false);
                        setSignatureOption(savedData.signatureOption || 'existing');
                        setSignatureData(savedData.signatureData || '');

                        // Restore check restrictions
                        setIncludeNoChangesText(savedData.includeNoChangesText !== undefined ? savedData.includeNoChangesText : true);
                        setIncludePillHeaders(savedData.includePillHeaders !== undefined ? savedData.includePillHeaders : true);

                        setSupplierDeductionRate(savedData.supplierDeductionRate ?? null);
                        setUseSupplierDeductionRate(savedData.useSupplierDeductionRate ?? false);

                        toast.success(t('DataRestoredSuccessfully'));
                    } else {
                        clearSavedFormData();
                    }
                });
            }
        }
    }, [open, caseId]);

    // Prevent page refresh when PDF modal is open or when form has data
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (showPdfModal || (open && formData.payTo && formData.amountInNumbers > 0)) {
                // Save data before leaving
                saveFormDataToLocalStorage();

                e.preventDefault();
                e.returnValue = t('ClosePDFPreviewMessage');
                return t('ClosePDFPreviewMessage');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [showPdfModal, open, formData, caseId, t]);

    // Auto-convert amount to words when amount or withholding tax changes
    useEffect(() => {
        if (formData.amountInNumbers > 0) {
            // Calculate final amount after withholding tax
            const finalAmount = formData.amountInNumbers - (formData.withholdingTax || 0);
            const hebrewWords = convertToHebrewWords(finalAmount);
            setFormData(prev => ({
                ...prev,
                amountInWords: hebrewWords
            }));
        }
    }, [formData.amountInNumbers, formData.withholdingTax]);

    useEffect(() => {
        if (!useSupplierDeductionRate || supplierDeductionRate === null) {
            return;
        }

        setFormData((prev) => {
            const gross = Number(prev.amountInNumbers) || 0;
            const calculated = calculateWithholdingFromRate(gross, supplierDeductionRate);
            const prevWh = Number(prev.withholdingTax);
            const prevNorm = Number.isFinite(prevWh) ? prevWh : 0;
            if (Number(calculated.toFixed(2)) === Number(prevNorm.toFixed(2))) {
                return prev;
            }

            return {
                ...prev,
                withholdingTax: calculated
            };
        });
    }, [useSupplierDeductionRate, supplierDeductionRate, formData.amountInNumbers]);

    const handleInputChange = (field, value) => {
        if (field === 'withholdingTax') {
            setUseSupplierDeductionRate(false);
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePayToChange = (newValue) => {
        // Check if a supplier account is selected
        if (!formData.supplierAccountId) {
            toast.warning(t('SelectSupplierFirst'));
            return;
        }
        // Just update the form data - no dialog on character change
        handleInputChange('payTo', newValue);
    };

    const isCurrentFormValid = () =>
        formData.payTo &&
        formData.amountInNumbers > 0 &&
        formData.magneticLineData &&
        formData.tBankAccountNumber;

    const buildCheckItemFromForm = () => ({
        id: createQueueItemId(),
        payTo: formData.payTo,
        amountInNumbers: formData.amountInNumbers,
        amountInWords: formData.amountInWords,
        dueDate: formData.dueDate,
        details: formData.details,
        supplierAccountId: formData.supplierAccountId,
        withholdingTax: formData.withholdingTax || 0,
        checkNumber: formData.magneticLineData,
    });

    const getNextCheckNumber = (currentNumber, offset = 1) => {
        const parsed = parseInt(currentNumber, 10);
        if (Number.isFinite(parsed)) {
            return (parsed + offset).toString();
        }
        return ((lastCheckNumber || 0) + checkQueue.length + offset).toString();
    };

    const resetFormAfterQueueAdd = (nextCheckNumber) => {
        setFormData((prev) => ({
            ...prev,
            amountInNumbers: 0,
            amountInWords: '',
            details: '',
            withholdingTax: useSupplierDeductionRate && supplierDeductionRate ? 0 : prev.withholdingTax,
            magneticLineData: nextCheckNumber,
        }));
    };

    const addToQueue = () => {
        if (!isCurrentFormValid()) {
            toast.error('Please fill in all required fields including bank account');
            return;
        }

        setCheckQueue((prev) => [...prev, buildCheckItemFromForm()]);
        resetFormAfterQueueAdd(getNextCheckNumber(formData.magneticLineData));
        toast.success(t('CheckAddedToQueue'));
    };

    const addMultipleToQueue = () => {
        if (!isCurrentFormValid()) {
            toast.error('Please fill in all required fields including bank account');
            return;
        }

        const count = Math.max(1, Math.min(50, parseInt(quickAddCount, 10) || 1));
        const startNumber = parseInt(formData.magneticLineData, 10);
        const baseNumber = Number.isFinite(startNumber) ? startNumber : (lastCheckNumber || 0) + 1;
        const template = buildCheckItemFromForm();
        const items = Array.from({ length: count }, (_, index) => ({
            ...template,
            id: createQueueItemId(),
            checkNumber: (baseNumber + index).toString(),
        }));

        setCheckQueue((prev) => [...prev, ...items]);
        resetFormAfterQueueAdd((baseNumber + count).toString());
        toast.success(t('ChecksAddedToQueue', { count: items.length }));
    };

    const removeFromQueue = (itemId) => {
        setCheckQueue((prev) => prev.filter((item) => item.id !== itemId));
    };

    const loadQueueItemIntoForm = (item) => {
        setFormData((prev) => ({
            ...prev,
            payTo: item.payTo,
            amountInNumbers: item.amountInNumbers,
            amountInWords: item.amountInWords || '',
            dueDate: item.dueDate ? new Date(item.dueDate) : new Date(),
            details: item.details || '',
            supplierAccountId: item.supplierAccountId || '',
            withholdingTax: item.withholdingTax || 0,
            magneticLineData: item.checkNumber,
        }));
    };

    const editQueueItem = (item) => {
        loadQueueItemIntoForm(item);
        removeFromQueue(item.id);
    };

    const getChecksToPrint = () => {
        const items = [...checkQueue];
        if (isCurrentFormValid()) {
            items.push(buildCheckItemFromForm());
        }
        return items;
    };

    const buildPrintRequestData = (item) => {
        const finalCheckAmount = item.amountInNumbers - (item.withholdingTax || 0);
        return {
            payTo: item.payTo,
            amountInNumbers: finalCheckAmount,
            amountInWords: item.amountInWords || convertToHebrewWords(finalCheckAmount),
            dueDate: item.dueDate,
            checkNumber: item.checkNumber,
            details: item.details,
            withholdingTax: item.withholdingTax || 0,
            supplierAccountId: item.supplierAccountId,
        };
    };

    const buildSharedPrintSettings = () => ({
        caseId: caseId,
        uploadDate: formData.uploadDate,
        checkRestriction: formData.checkRestriction,
        isTransferable: formData.isTransferable,
        maxValidityDate: formData.maxValidityDate?.toISOString(),
        maxAmount: formData.maxAmount,
        addPayment: formData.addPayment,
        tBankAccountNumber: formData.tBankAccountNumber,
        includeSignature: includeSignature,
        signatureData: getSignatureData(),
        includeNoChangesText: includeNoChangesText,
        includePillHeaders: includePillHeaders,
    });

    const handlePrint = () => {
        const checksToPrint = getChecksToPrint();
        if (checksToPrint.length === 0) {
            toast.error('Please fill in all required fields including bank account');
            return;
        }

        if (!formData.tBankAccountNumber) {
            toast.error('Please fill in all required fields including bank account');
            return;
        }

        setPrintedChecksPreview(checksToPrint.map((item) => ({
            ...item,
            ...buildPrintRequestData(item),
        })));
        setPreviewPrintSettings({
            signatureSrc: includeSignature ? getSignatureData() : '',
            includeNoChangesText,
        });
        setPdfUrl('');
        setShowPdfModal(true);
    };

    const confirmPrint = async () => {
        setLoading(true);

        try {
            // Check if pay-to name has changed and show confirmation dialog
            if (formData.payTo !== originalPayToName && formData.supplierAccountId && originalPayToName) {
                const result = await Swal.fire({
                    title: t('UpdateAccountCard'),
                    text: t('UpdateAccountCardMessage'),
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: t('Yes'),
                    cancelButtonText: t('No'),
                    confirmButtonColor: '#304FFF',
                    cancelButtonColor: '#6c757d'
                });

                if (result.isConfirmed) {
                    try {
                        // Find the current account card to get the old name
                        const currentAccountCard = findAccountCardByOptionValue(
                            accountCardOptions,
                            formData.supplierAccountId
                        );
                        const oldName = currentAccountCard ? currentAccountCard.name : '';

                        // Update the account card with the new name using the main UpdateAccountCard function
                        await EnumsService.UpdateAccountCard(
                            client.caseId,
                            formData.supplierAccountId.toString(), // OldCardId
                            oldName, // OldName
                            formData.supplierAccountId.toString(), // CardId (same)
                            formData.payTo, // New Name
                            formData.supplierAccountCaseId, // AccountCaseId
                            null, // PalCode
                            token,
                            formData.supplierAccountPhoneNumber,
                            formData.supplierAccountLocation,
                            formData.supplierAccountEmail
                        );

                        toast.success(t('AccountCardUpdatedSuccessfully'));

                        // Refresh account cards list
                        await fetchAccountCards();
                    } catch (error) {
                        console.error('Error updating account card:', error);
                        toast.error(t('ErrorUpdatingAccountCard'));
                    }
                }
            }
            // Save new supplier account if needed
            if (addNewSupplierAccountDocument) {
                await EnumsService.AddAccountCard(
                    client.caseId,
                    formData.supplierAccountId.toString(),
                    formData.supplierAccountName,
                    formData.supplierAccountCaseId,
                    "ספקים",
                    token,
                    formData.supplierAccountPhoneNumber,
                    formData.supplierAccountLocation,
                    formData.supplierAccountEmail
                );
            } else if (formData.supplierAccountId) {
                await EnumsService.UpdateAccountCardCaseId(
                    client.caseId,
                    formData.supplierAccountId.toString(),
                    formData.supplierAccountCaseId,
                    formData.supplierAccountEmail,
                    formData.supplierAccountPhoneNumber,
                    formData.supplierAccountLocation,
                    token
                );
            }

            const checksToPrint = getChecksToPrint();
            const sharedSettings = buildSharedPrintSettings();
            let htmlContent;

            if (checksToPrint.length === 1) {
                const checkRequest = buildPrintRequestData(checksToPrint[0]);
                htmlContent = await DocumentService.PrintChecks({
                    ...sharedSettings,
                    ...checkRequest,
                }, token);
            } else {
                htmlContent = await DocumentService.PrintChecksBatch({
                    ...sharedSettings,
                    checks: checksToPrint.map((item) => buildPrintRequestData(item)),
                }, token);
            }

            setPdfUrl(htmlContent);
            handlePrintFromPreview(htmlContent);

            if (formData.addPayment) {
                if (checksToPrint.length > 1) {
                    toast.success(t('ChecksPrintedAndPaymentsAddedSuccessfully', { count: checksToPrint.length }));
                } else {
                    toast.success(t('CheckPrintedAndPaymentAddedSuccessfully'));
                }
            } else if (checksToPrint.length > 1) {
                toast.success(t('ChecksPrintedSuccessfully', { count: checksToPrint.length }));
            } else {
                toast.success(t('CheckPrintedSuccessfully'));
            }

            clearSavedFormData();
            setCheckQueue([]);

            setFormData(prev => ({
                ...prev,
                amountInNumbers: 0,
                details: '',
                withholdingTax: 0
            }));

            await fetchLastCheckNumber();

            if (onPrintSuccess) {
                onPrintSuccess();
            }

            setShowPdfModal(false);
            setPrintedChecksPreview([]);
            setPreviewPrintSettings({ signatureSrc: '', includeNoChangesText: true });
            setCheckDetailView(null);

        } catch (error) {
            console.error('Error printing check:', error);
            toast.error(t('ErrorPrintingCheck'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Clear saved data when manually closing
        clearSavedFormData();

        setFormData({
            payTo: '',
            amountInNumbers: 0,
            amountInWords: '',
            dueDate: new Date(),
            uploadDate: new Date(),
            checkRestriction: null,
            isTransferable: true,
            maxValidityDate: null,
            maxAmount: null,
            magneticLineData: '0101234567890123456',
            supplierAccountId: '',
            supplierAccountName: '',
            supplierAccountCaseId: '',
            supplierAccountEmail: '',
            supplierAccountPhoneNumber: '',
            supplierAccountLocation: '',
            addPayment: true,
            tBankAccountNumber: '',
            details: '',
            withholdingTax: 0
        });
        setSelectedTBank(null);
        setOriginalPayToName('');
        setShowPdfModal(false);
        setIncludeSignature(false);
        setSignatureOption('existing');
        setSignatureData('');
        setShowSignatureModal(false);
        setIncludeNoChangesText(true);
        setIncludePillHeaders(true);
        setPdfUrl('');
        setPrintedChecksPreview([]);
        setPreviewPrintSettings({ signatureSrc: '', includeNoChangesText: true });
        setCheckDetailView(null);
        setSupplierDeductionRate(null);
        setUseSupplierDeductionRate(false);
        setCheckQueue([]);
        setQuickAddCount(2);
        setInitialSelectionLoaded(false);
        onClose();
    };

    const handleClosePdfModal = async () => {
        if (pdfUrl) {
            const result = await Swal.fire({
                title: t('ClosePDFPreview'),
                text: t('ClosePDFPreviewMessage'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: t('Yes'),
                cancelButtonText: t('No'),
                confirmButtonColor: '#304FFF',
                cancelButtonColor: '#6c757d'
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        setShowPdfModal(false);
        setPdfUrl('');
        setPrintedChecksPreview([]);
        setPreviewPrintSettings({ signatureSrc: '', includeNoChangesText: true });
        setCheckDetailView(null);
    };

    const handlePrintFromPreview = (htmlContent = pdfUrl) => {
        if (!htmlContent) return;

        // Print through a hidden iframe instead of window.open.
        // window.open runs after an await (the print API call) so it is no longer part of
        // the original click gesture and popup blockers silently block the blank window —
        // which breaks printing (especially for multiple/batch checks). An iframe avoids that.
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.opacity = '0';
        document.body.appendChild(iframe);

        const win = iframe.contentWindow;
        const doc = win?.document || iframe.contentDocument;
        if (!win || !doc) {
            iframe.remove();
            // Fallback to a new window if the iframe could not be created
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(htmlContent);
                printWindow.document.close();
                setTimeout(() => printWindow.print(), 500);
            }
            return;
        }

        let printed = false;
        const printOnce = () => {
            if (printed) return;
            printed = true;
            try {
                win.focus();
                win.print();
            } catch (err) {
                console.error('Error printing checks:', err);
            }
            // Remove the iframe after the print dialog has had time to open
            setTimeout(() => iframe.remove(), 1000);
        };

        doc.open();
        doc.write(htmlContent);
        doc.close();

        // Print once the iframe content (images/fonts) has loaded, with a fallback timer
        win.onload = () => setTimeout(printOnce, 300);
        setTimeout(printOnce, 1200);
    };

    // Signature functions
    const handleSignatureChange = () => {
        if (sigPad.current) {
            const signatureDataURL = sigPad.current.toDataURL();
            setSignatureData(signatureDataURL);
        }
    };

    const clearSignature = () => {
        if (sigPad.current) {
            sigPad.current.clear();
            setSignatureData('');
        }
    };

    const saveSignature = () => {
        if (signatureData) {
            setShowSignatureModal(false);
            toast.success(t('SignatureSavedSuccessfully'));
        } else {
            toast.error(t('PleaseDrawSignature'));
        }
    };

    const getSignatureData = () => {
        // If signature is not included, return empty string
        if (!includeSignature) {
            return '';
        }

        if (signatureOption === 'existing' && client?.signature) {
            return client.signature;
        } else if (signatureOption === 'new' && signatureData) {
            return signatureData;
        }
        return '';
    };

    const handleSignatureOptionChange = (newOption) => {
        setSignatureOption(newOption);

        // When switching to existing signature, clear any new signature data
        if (newOption === 'existing') {
            setSignatureData('');
            // Clear the signature pad if it exists
            if (sigPad.current) {
                sigPad.current.clear();
            }
        }
        // When switching to new signature, we keep the existing signatureData
        // so user can continue editing if they had already created one
    };

    // Bank-related handlers
    const bankOptions = Object.keys(Constants.BankNames).map((key) => ({
        value: key,
        label: `${Constants.BankNames[key]} - ${key}`,
    }));

    const handleOpenCreateModal = () => {
        setCreateBankModal(true);
    };

    const handleCloseCreateModal = () => {
        setCreateBankModal(false);
        setSelectedBankForCreate(null);
        setBankBranch("");
        setBankAccountNumber("");
        setCardForNewBank(null);
    };

    const handleChangeBankForCreate = (value) => {
        setSelectedBankForCreate(value);
    };

    const handleChangeCreateBank = (field, value) => {
        switch (field) {
            case 'bankBranch':
                setBankBranch(value);
                break;
            case 'bankAccountNumber':
                setBankAccountNumber(value);
                break;
            default:
                break;
        }
    };

    const handleChangeSetCardForNewBank = (cardId) => {
        setCardForNewBank(cardId);
    };

    const handleAddBank = async () => {
        try {
            const bankName = Constants.BankNames[parseInt(selectedBankForCreate.value)];
            const bankCode = selectedBankForCreate.value;

            if (!selectedBankForCreate || !bankBranch || !bankAccountNumber) {
                toast.error(t("PleaseFillAllFields"));
                return;
            }

            const response = await EnumsService.AddTBank(
                client.caseId,
                bankName,
                bankCode,
                bankBranch,
                bankAccountNumber,
                null, // No currency for this modal
                cardForNewBank.toString(),
                token
            );

            if (response.ok) {
                toast.success(t("BankAccountAddedSuccessfully"));
                fetchTBanks(); // Refresh TBanks data
                handleCloseCreateModal();
            } else {
                toast.error(t("FailedToAddBankAccount"));
            }
        } catch (error) {
            console.error(error);
            toast.error(t("ErrorAddingBankAccount"));
        }
    };

    const adjustQuickAddCount = (delta) => {
        setQuickAddCount((prev) => {
            const current = Math.max(1, parseInt(prev, 10) || 1);
            return Math.min(50, Math.max(1, current + delta));
        });
    };

    const totalChecksToPrint = checkQueue.length + (isCurrentFormValid() ? 1 : 0);
    const canPrintChecks = totalChecksToPrint > 0 && !!formData.tBankAccountNumber;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    className: nestedStyles.nestedPaper,
                    elevation: 0,
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
                        },
                    },
                }}
            >
                <div
                    className={`${nestedStyles.shell} ${filterDirection === 'rtl' ? nestedStyles.rtl : nestedStyles.ltr}`}
                    dir={filterDirection}
                >
                    <div className={nestedStyles.header}>
                            <div className={nestedStyles.headerTitle}>
                                <PrintIcon sx={{ fontSize: 22 }} />
                                <span>
                                    {totalChecksToPrint > 1 ? t('PrintChecks') : t('PrintCheck')}
                                </span>
                                {checkQueue.length > 0 && (
                                    <span className={nestedStyles.headerMeta}>
                                        {t('ChecksInQueue', { count: checkQueue.length })}
                                    </span>
                                )}
                            </div>
                            <IconButton onClick={handleClose} className={nestedStyles.closeIcon}>
                                <CancelIcon className={nestedStyles.closeIcon} />
                            </IconButton>
                        <div />
                    </div>

                <DialogContent
                    className={nestedStyles.body}
                    dividers={false}
                    sx={{
                        p: '0 !important',
                        direction: direction,
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                        ...scrollbarSx,
                    }}
                >
                    <Paper elevation={0} className={nestedStyles.sectionCard}>
                        <Box className={nestedStyles.sectionCardHeader} sx={{ direction: direction }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <QueueIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                                <Typography variant="subtitle2" className={nestedStyles.sectionTitle} sx={{ m: 0 }}>
                                    {t('ChecksQueue')}
                                </Typography>
                                {checkQueue.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={checkQueue.length}
                                        sx={{
                                            height: 20,
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            bgcolor: '#304FFF',
                                            color: 'white',
                                        }}
                                    />
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Tooltip title={t('QuickAddChecksTooltip')} arrow>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: '#eef1ff',
                                        borderRadius: '8px',
                                        border: '1px solid #d8deff',
                                        overflow: 'hidden',
                                    }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => adjustQuickAddCount(-1)}
                                            disabled={loading || (parseInt(quickAddCount, 10) || 1) <= 1}
                                            sx={{ borderRadius: 0, p: 0.5 }}
                                        >
                                            <RemoveIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <Typography sx={{
                                            minWidth: 28,
                                            textAlign: 'center',
                                            fontSize: '13px',
                                            fontWeight: 700,
                                            color: '#304FFF',
                                            px: 0.5,
                                        }}>
                                            {parseInt(quickAddCount, 10) || 1}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => adjustQuickAddCount(1)}
                                            disabled={loading || (parseInt(quickAddCount, 10) || 1) >= 50}
                                            sx={{ borderRadius: 0, p: 0.5 }}
                                        >
                                            <AddIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                </Tooltip>

                                <Tooltip title={t('QuickAddChecksTooltip')} arrow>
                                    <span>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={addMultipleToQueue}
                                            disabled={loading || !isCurrentFormValid()}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                borderRadius: '8px',
                                                borderColor: '#c5ceff',
                                                color: '#304FFF',
                                                py: 0.4,
                                                minWidth: 'auto',
                                                px: 1.25,
                                                '&:hover': { borderColor: '#304FFF', bgcolor: '#f0f3ff' },
                                            }}
                                        >
                                            {t('QuickAddChecks')}
                                        </Button>
                                    </span>
                                </Tooltip>

                                <Tooltip title={t('AddToQueueTooltip')} arrow>
                                    <span>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={addToQueue}
                                            disabled={loading || !isCurrentFormValid()}
                                            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                borderRadius: '8px',
                                                bgcolor: '#304FFF',
                                                boxShadow: 'none',
                                                py: 0.5,
                                                px: 1.25,
                                                '&:hover': { bgcolor: '#243dcc', boxShadow: '0 2px 8px rgba(48,79,255,0.25)' },
                                            }}
                                        >
                                            {t('AddToQueue')}
                                        </Button>
                                    </span>
                                </Tooltip>

                                {checkQueue.length > 0 && (
                                    <Tooltip title={t('ClearQueue')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => setCheckQueue([])}
                                            sx={{ color: '#999', '&:hover': { color: '#e53935', bgcolor: '#fff5f5' } }}
                                        >
                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {checkQueue.length > 0 ? (
                            <Box sx={{
                                display: 'flex',
                                gap: 1,
                                p: 1.25,
                                overflowX: 'auto',
                                pb: 1.5,
                                ...scrollbarSx,
                            }}>
                                {checkQueue.map((item, index) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            width: 196,
                                            flexShrink: 0,
                                            borderRadius: '10px',
                                            border: '1px solid #e8ebff',
                                            bgcolor: 'white',
                                            overflow: 'hidden',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                borderColor: '#304FFF',
                                                boxShadow: '0 3px 12px rgba(48, 79, 255, 0.12)',
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 1, p: 1, alignItems: 'center' }}>
                                            <CheckQueueThumbnail item={item} />
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="caption" sx={{ color: '#304FFF', fontWeight: 700, fontSize: '10px' }}>
                                                    #{item.checkNumber}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px', lineHeight: 1.25, mb: 0.25 }} noWrap>
                                                    {item.payTo}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#555', fontSize: '11px', display: 'block' }} noWrap>
                                                    ₪{(item.amountInNumbers - (item.withholdingTax || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#999', fontSize: '10px' }}>
                                                    {format(item.dueDate ? new Date(item.dueDate) : new Date(), 'dd/MM/yy')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', borderTop: '1px solid #f2f4ff' }}>
                                            <Tooltip title={t('EditCheckInQueue')} arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => editQueueItem(item)}
                                                    sx={{ flex: 1, borderRadius: 0, py: 0.5, color: '#304FFF' }}
                                                >
                                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('RemoveFromQueue')} arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeFromQueue(item.id)}
                                                    sx={{ flex: 1, borderRadius: 0, py: 0.5, color: '#e53935' }}
                                                >
                                                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{
                                px: 2,
                                py: 1.25,
                                color: '#8a94b8',
                                fontSize: '12px',
                                textAlign: 'center',
                                fontStyle: 'italic',
                            }}>
                                {t('QueueEmptyHint')}
                            </Box>
                        )}
                    </Paper>

                    {/* Form Section */}
                    <Paper elevation={0} className={nestedStyles.sectionCard}>
                        <Box className={nestedStyles.sectionBody}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1,
                                direction: direction
                            }}>
                                <Typography variant="h6" className={nestedStyles.sectionTitle} sx={{ m: 0, fontSize: '15px' }}>
                                    <PreviewIcon sx={{ fontSize: 18 }} />
                                    {t('CheckDetails')} 
                                </Typography>

                                {/* Auto-save indicator */}
                                {isDataSaved && (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: '#4caf50',
                                        fontSize: '12px',
                                        direction: direction
                                    }}>
                                        <Box sx={{
                                            width: '8px',
                                            height: '8px',
                                            backgroundColor: '#4caf50',
                                            borderRadius: '50%',
                                            animation: 'pulse 1s infinite',
                                            '@keyframes pulse': {
                                                '0%': { opacity: 1 },
                                                '50%': { opacity: 0.5 },
                                                '100%': { opacity: 1 }
                                            }
                                        }} />
                                        {t('DataSaved')}
                                    </Box>
                                )}
                            </Box>

                            <Grid container spacing={2} className={filterDirection === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}>
                                {/* Row 1: Basic Check Information - Pay To, Amount, Due Date */}
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('Supplier')} *
                                    </Typography>

                                    <div className={getAutocompleteFieldClassName(supplierMissing)}>
                                        <AutocompleteSelectFilter
                                            options={formattedAccountCardOptions}
                                            selectedValues={
                                                formData.supplierAccountId
                                                    ? formattedAccountCardOptions.find(
                                                        (opt) =>
                                                            String(opt.value) === String(formData.supplierAccountId)
                                                    ) || null
                                                    : null
                                            }
                                            onChange={(_, newValue) => handleSupplierAccountChange(newValue)}
                                            placeholder={t('SelectSupplier')}
                                            isMultiple={false}
                                            direction={filterDirection}
                                            searchable
                                            portalMenu
                                        />
                                    </div>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('Amount')} *
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={formData.amountInNumbers}
                                        onChange={(e) => handleInputChange('amountInNumbers', parseFloat(e.target.value) || 0)}
                                        placeholder="0.00"
                                        variant="standard"
                                        className={getFieldClassName(amountMissing)}
                                        inputProps={{ step: "0.01", min: "0", style: { textAlign: fieldTextAlign } }}
                                        InputProps={comboInputProps}
                                    />
                                    {formData.withholdingTax > 0 && (
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            color: '#666',
                                            mt: 0.5,
                                            direction: direction,
                                            textAlign: direction === 'ltr' ? 'left' : 'right',
                                            fontSize: '11px'
                                        }}>
                                            {t('FinalCheckAmount')}: ₪{(formData.amountInNumbers - formData.withholdingTax).toFixed(2)}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('DuePaymentDate')} *
                                    </Typography>
                                    <CustomDatePicker
                                        date={formData.dueDate}
                                        onDateChange={(date) => {
                                            if (date) {
                                                handleInputChange('dueDate', date);
                                            }
                                        }}
                                        height="40px"
                                        textFieldClassName={fieldStyles.styleInput}
                                        inputSx={datePickerInputSx}
                                    />
                                </Grid>

                                {/* Row 2: Bank & Account Information */}
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('BankAccount')} *
                                    </Typography>

                                    <div className={getAutocompleteFieldClassName(bankAccountMissing)}>
                                        <AutocompleteSelectFilter
                                            options={formattedTBankOptions}
                                            selectedValues={selectedTBank}
                                            onChange={(_, newValue) => handleTBankChange(newValue)}
                                            placeholder={t('SelectBankAccount')}
                                            isMultiple={false}
                                            direction={filterDirection}
                                            searchable
                                            portalMenu
                                        />
                                    </div>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('CheckNumber')} *
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value={formData.magneticLineData}
                                        onChange={(e) => handleInputChange('magneticLineData', e.target.value)}
                                        placeholder={t('EnterCheckNumber')}
                                        variant="standard"
                                        className={fieldStyles.styleInput}
                                        InputProps={comboInputProps}
                                        inputProps={{ style: { textAlign: fieldTextAlign } }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('UploadDate')}
                                    </Typography>
                                    <CustomDatePicker
                                        date={formData.uploadDate}
                                        onDateChange={(date) => {
                                            if (date) {
                                                handleInputChange('uploadDate', date);
                                            }
                                        }}
                                        height="40px"
                                        textFieldClassName={fieldStyles.styleInput}
                                        inputSx={datePickerInputSx}
                                    />
                                </Grid>

                                {/* Row 3: Check Details and Restrictions */}
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('CheckRestriction')}
                                    </Typography>
                                    <AutocompleteSelectFilter
                                        options={restrictionFilterOptions}
                                        selectedValues={
                                            restrictionFilterOptions.find(
                                                (opt) => String(opt.value) === String(formData.checkRestriction ?? '')
                                            ) || restrictionFilterOptions[0]
                                        }
                                        onChange={(_, newValue) => handleInputChange('checkRestriction', newValue?.value || null)}
                                        placeholder={t('CheckRestriction')}
                                        isMultiple={false}
                                        direction={filterDirection}
                                        portalMenu
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('Details')}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value={formData.details}
                                        onChange={(e) => handleInputChange('details', e.target.value)}
                                        placeholder={t('EnterDetails')}
                                        variant="standard"
                                        className={fieldStyles.styleInput}
                                        InputProps={comboInputProps}
                                        inputProps={{ style: { textAlign: fieldTextAlign } }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 500,
                                        color: '#666',
                                        mb: 1,
                                        fontSize: '13px',
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        direction: direction
                                    }}>
                                        {t('WithholdingTax')} (₪)
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={formData.withholdingTax}
                                        onChange={(e) => handleInputChange('withholdingTax', parseFloat(e.target.value) || 0)}
                                        placeholder="0.00"
                                        variant="standard"
                                        className={fieldStyles.styleInput}
                                        inputProps={{ step: "0.01", min: "0", style: { textAlign: fieldTextAlign } }}
                                        InputProps={comboInputProps}
                                        disabled={useSupplierDeductionRate}
                                    />
                                    {supplierDeductionRate !== null && (
                                        <Box sx={{
                                            mt: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            alignItems: direction === 'ltr' ? 'flex-start' : 'flex-end'
                                        }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={useSupplierDeductionRate}
                                                        onChange={(e) => setUseSupplierDeductionRate(e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label={t('UseSupplierDeductionRate', {
                                                    rate: supplierDeductionRate,
                                                    defaultValue: `Use supplier deduction rate (${supplierDeductionRate}%)`
                                                })}
                                                sx={{
                                                    direction: direction,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '12px'
                                                    }
                                                }}
                                            />
                                            {useSupplierDeductionRate && (
                                                <Typography variant="caption" sx={{
                                                    direction: direction,
                                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                                    color: '#666'
                                                }}>
                                                    {t('CalculatedWithholdingAmount', {
                                                        amount: supplierWithholdingDisplayAmount.toFixed(2),
                                                        defaultValue: `Calculated withholding: ₪${supplierWithholdingDisplayAmount.toFixed(2)}`
                                                    })}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Grid>

                                {/* Row 4: Payment Options — one row */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        direction: direction,
                                    }}>
                                        <Checkbox
                                            checked={formData.addPayment}
                                            onChange={(e) => handleInputChange('addPayment', e.target.checked)}
                                            size="small"
                                            sx={{
                                                color: 'var(--modern-primary)',
                                                padding: '2px',
                                                mt: '-2px',
                                                '&.Mui-checked': {
                                                    color: 'var(--modern-primary)',
                                                },
                                            }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" sx={{
                                                fontWeight: 600,
                                                color: '#666',
                                                fontSize: '13px',
                                                textAlign: 'start',
                                                direction: direction,
                                            }}>
                                                {t('AddPayment')}
                                            </Typography>
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                color: '#888',
                                                mt: 0.25,
                                                direction: direction,
                                                textAlign: 'start',
                                                lineHeight: 1.35,
                                            }}>
                                                {t('AddPaymentDescription')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        direction: direction,
                                    }}>
                                        <Checkbox
                                            checked={includeNoChangesText}
                                            onChange={(e) => setIncludeNoChangesText(e.target.checked)}
                                            size="small"
                                            sx={{
                                                color: 'var(--modern-primary)',
                                                padding: '2px',
                                                mt: '-2px',
                                                '&.Mui-checked': {
                                                    color: 'var(--modern-primary)',
                                                },
                                            }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" sx={{
                                                fontWeight: 600,
                                                color: '#666',
                                                fontSize: '13px',
                                                textAlign: 'start',
                                                direction: direction,
                                            }}>
                                                {t('IncludeNoChangesText')}
                                            </Typography>
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                color: '#888',
                                                mt: 0.25,
                                                direction: direction,
                                                textAlign: 'start',
                                                lineHeight: 1.35,
                                            }}>
                                                {t('IncludeNoChangesTextDescription')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        direction: direction,
                                    }}>
                                        <Checkbox
                                            checked={includePillHeaders}
                                            onChange={(e) => setIncludePillHeaders(e.target.checked)}
                                            size="small"
                                            sx={{
                                                color: 'var(--modern-primary)',
                                                padding: '2px',
                                                mt: '-2px',
                                                '&.Mui-checked': {
                                                    color: 'var(--modern-primary)',
                                                },
                                            }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" sx={{
                                                fontWeight: 600,
                                                color: '#666',
                                                fontSize: '13px',
                                                textAlign: 'start',
                                                direction: direction,
                                            }}>
                                                {t('IncludePillHeaders')}
                                            </Typography>
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                color: '#888',
                                                mt: 0.25,
                                                direction: direction,
                                                textAlign: 'start',
                                                lineHeight: 1.35,
                                            }}>
                                                {t('IncludePillHeadersDescription')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Signature Options — radios column + preview beside */}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'flex-start',
                                            gap: 0.5,
                                            direction: direction,
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 0.5,
                                            direction: direction,
                                            flex: '0 1 180px',
                                            minWidth: 160,
                                        }}>
                                            <Checkbox
                                                checked={includeSignature}
                                                onChange={(e) => setIncludeSignature(e.target.checked)}
                                                size="small"
                                                sx={{
                                                    color: 'var(--modern-primary)',
                                                    padding: '2px',
                                                    mt: '-2px',
                                                    '&.Mui-checked': {
                                                        color: 'var(--modern-primary)',
                                                    },
                                                }}
                                            />
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="body2" sx={{
                                                    fontWeight: 600,
                                                    color: '#666',
                                                    fontSize: '13px',
                                                    textAlign: 'start',
                                                    direction: direction,
                                                }}>
                                                    {t('IncludeSignature')}
                                                </Typography>
                                                {includeSignature && (
                                                    <Typography variant="caption" sx={{
                                                        display: 'block',
                                                        color: '#888',
                                                        mt: 0.15,
                                                        direction: direction,
                                                        textAlign: 'start',
                                                        lineHeight: 1.35,
                                                    }}>
                                                        {t('SignatureOption')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {includeSignature && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    direction: direction,
                                                    flex: '1 1 280px',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <RadioGroup
                                                    value={signatureOption}
                                                    onChange={(e) => handleSignatureOptionChange(e.target.value)}
                                                    sx={{
                                                        direction: direction,
                                                        flex: '0 0 auto',
                                                        '& .MuiFormControlLabel-root': {
                                                            margin: 0,
                                                            mb: 0.25,
                                                        },
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="existing"
                                                        control={<Radio size="small" sx={{ color: 'var(--modern-primary)', '&.Mui-checked': { color: 'var(--modern-primary)' } }} />}
                                                        label={t('UseExistingSignature')}
                                                        sx={{
                                                            direction: direction,
                                                            '& .MuiFormControlLabel-label': {
                                                                textAlign: 'start',
                                                                fontSize: '13px',
                                                                fontWeight: 500,
                                                                color: '#666',
                                                            },
                                                        }}
                                                    />
                                                    <FormControlLabel
                                                        value="new"
                                                        control={<Radio size="small" sx={{ color: 'var(--modern-primary)', '&.Mui-checked': { color: 'var(--modern-primary)' } }} />}
                                                        label={t('CreateNewSignature')}
                                                        sx={{
                                                            direction: direction,
                                                            '& .MuiFormControlLabel-label': {
                                                                textAlign: 'start',
                                                                fontSize: '13px',
                                                                fontWeight: 500,
                                                                color: '#666',
                                                            },
                                                        }}
                                                    />
                                                </RadioGroup>

                                                {signatureOption === 'existing' && client?.signature && (
                                                    <Box
                                                        component="img"
                                                        src={client.signature}
                                                        alt="Existing Signature"
                                                        sx={{
                                                            maxWidth: 200,
                                                            maxHeight: 60,
                                                            objectFit: 'contain',
                                                            border: '1px solid #e5e7f6',
                                                            borderRadius: '4px',
                                                            bgcolor: '#fff',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                )}

                                                {signatureOption === 'new' && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            direction: direction,
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setShowSignatureModal(true);
                                                                if (!signatureData) {
                                                                    setTimeout(() => {
                                                                        if (sigPad.current) {
                                                                            sigPad.current.clear();
                                                                        }
                                                                    }, 100);
                                                                }
                                                            }}
                                                            sx={{
                                                                borderRadius: '8px',
                                                                textTransform: 'none',
                                                                fontSize: '13px',
                                                                px: 2,
                                                                py: 1,
                                                                borderColor: '#304FFF',
                                                                color: '#304FFF',
                                                            }}
                                                        >
                                                            {signatureData ? t('EditSignature') : t('CreateSignature')}
                                                        </Button>
                                                        {signatureData && (
                                                            <Box
                                                                component="img"
                                                                src={signatureData}
                                                                alt="Signature"
                                                                sx={{
                                                                    maxWidth: 200,
                                                                    maxHeight: 60,
                                                                    objectFit: 'contain',
                                                                    border: '1px solid #e5e7f6',
                                                                    borderRadius: '4px',
                                                                    bgcolor: '#fff',
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>

                    {/* Preview Section */}
                    <Paper elevation={0} className={nestedStyles.sectionCard}>
                        <Box className={nestedStyles.sectionBody}>

                            {/* Check Preview - Exact HTML Structure */}
                            <Box sx={{
                                border: '2px solid #304FFF',
                                borderRadius: '12px',
                                p: 3,
                                backgroundColor: 'white',
                                position: 'relative',
                                boxShadow: '0 4px 20px rgba(48, 79, 255, 0.08)',
                                overflow: 'auto',
                                ...scrollbarSx,
                            }}>
                                <Box sx={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    backgroundColor: 'white',
                                    transform: 'scale(1)',
                                    transformOrigin: 'top center',
                                    margin: '0 auto'
                                }}>
                                    <div style={{
                                        width: '210mm',
                                        height: '74mm',
                                        position: 'relative',
                                        fontFamily: 'Arial, Helvetica, sans-serif',
                                        background: 'white',
                                        direction: 'rtl'
                                    }}>
                                        {/* Main Check Container */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: '160mm',
                                            height: '72mm',
                                            border: '2px dotted #ccc',
                                            background: 'white',
                                            padding: '2.5mm 5.5mm',
                                            boxSizing: 'border-box',
                                            direction: 'rtl'
                                        }}>
                                            {/* Bank Details Placeholder - Top Left */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '2.5mm',
                                                left: '5.5mm',
                                                fontSize: '8pt',
                                                color: '#999',
                                                fontStyle: 'italic'
                                            }}>
                                                {t('BankDetailsPlaceholder')}
                                            </div>

                                            {/* Your Details Placeholder - Top Right */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '2.5mm',
                                                right: '5.5mm',
                                                fontSize: '8pt',
                                                color: '#999',
                                                fontStyle: 'italic'
                                            }}>
                                                {t('YourDetailsPlaceholder')}
                                            </div>


                                            {/* Check Restriction */}
                                            {formData.checkRestriction !== null && formData.checkRestriction !== 0 && (
                                                <>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '6mm',
                                                        right: '78.5mm',
                                                        width: '11mm',
                                                        height: '10mm',
                                                        pointerEvents: 'none'
                                                    }}>
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '3mm',
                                                            width: '11mm',
                                                            height: '0.5px',
                                                            background: '#000',
                                                            transform: 'rotate(-15deg)',
                                                            transformOrigin: 'left center'
                                                        }}></div>
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '6mm',
                                                            width: '11mm',
                                                            height: '0.5px',
                                                            background: '#000',
                                                            transform: 'rotate(-15deg)',
                                                            transformOrigin: 'left center'
                                                        }}></div>
                                                    </div>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '4mm',
                                                        right: '78.5mm',
                                                        width: '11mm',
                                                        height: '10mm',
                                                        textAlign: 'center',
                                                        fontSize: '5pt',
                                                        fontWeight: 'bold',
                                                        transform: 'rotate(-15deg)',
                                                        transformOrigin: 'center',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {restrictionOptions.find(opt => opt.value === formData.checkRestriction)?.label === 'קרוס בלבד' ? '' : restrictionOptions.find(opt => opt.value === formData.checkRestriction)?.label}
                                                    </div>
                                                </>
                                            )}

                                            {/* Pay To Line */}
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '39.5mm',
                                                left: '5.5mm',
                                                width: '108mm',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-end',
                                                borderBottom: '1px solid #000',
                                                paddingBottom: '1mm',
                                                fontSize: '10pt'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '4px',
                                                        height: '4px',
                                                        background: 'black',
                                                        marginBottom: '-5px'
                                                    }}></span>
                                                    <input
                                                        type="text"
                                                        value={formData.payTo}
                                                        onChange={(e) => handlePayToChange(e.target.value)}
                                                        style={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            fontSize: '10pt',
                                                            outline: 'none',
                                                            width: '105mm',
                                                            cursor: 'text',
                                                            borderBottom: '1px dashed #304FFF'
                                                        }}
                                                        title="Editable - Click to edit"
                                                    />
                                                </div>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '4px',
                                                    height: '4px',
                                                    background: 'black',
                                                    marginBottom: '-5px'
                                                }}></span>
                                            </div>

                                            {/* Small note underneath pay-to field */}
                                            {formData.supplierAccountId && (
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '37mm',
                                                    left: '5.5mm',
                                                    fontSize: '6pt',
                                                    color: '#666',
                                                    fontStyle: 'italic'
                                                }}>
                                                    {t('CanBeChanged')}
                                                </div>
                                            )}

                                            {/* Amount in Words */}
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '31.5mm',
                                                left: '5.5mm',
                                                width: '108mm',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-end',
                                                borderBottom: '1px solid #000',
                                                paddingBottom: '1mm',
                                                fontSize: '10pt'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '4px',
                                                        height: '4px',
                                                        background: 'black',
                                                        marginBottom: '-5px'
                                                    }}></span>
                                                    <span style={{ fontSize: '7px' }}>N.I.S</span>
                                                    <input
                                                        type="text"
                                                        value={formData.amountInWords}
                                                        readOnly
                                                        style={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            fontSize: formData.amountInWords.length > 50 ? '7pt' : formData.amountInWords.length > 30 ? '9pt' : '10pt',
                                                            outline: 'none',
                                                            width: '108mm',
                                                            cursor: 'default'
                                                        }}
                                                    />
                                                </div>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '4px',
                                                    height: '4px',
                                                    background: 'black',
                                                    marginBottom: '-5px'
                                                }}></span>
                                            </div>

                                            {/* Amount in Numbers */}
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '31.5mm',
                                                right: '5.5mm',
                                                width: '34mm',
                                                textAlign: 'right',
                                                fontSize: '10pt',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #000',
                                                paddingBottom: '1mm',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-end'
                                            }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '4px',
                                                    height: '4px',
                                                    background: 'black',
                                                    marginBottom: '-5px'
                                                }}></span>
                                                <input
                                                    type="number"
                                                    value={formData.amountInNumbers - (formData.withholdingTax || 0)}
                                                    onChange={(e) => {
                                                        const newFinalAmount = parseFloat(e.target.value) || 0;
                                                        const newOriginalAmount = newFinalAmount + (formData.withholdingTax || 0);
                                                        handleInputChange('amountInNumbers', newOriginalAmount);
                                                    }}
                                                    style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        fontSize: '10pt',
                                                        fontWeight: 'bold',
                                                        outline: 'none',
                                                        width: '80px',
                                                        textAlign: 'right',
                                                        cursor: 'text',
                                                        borderBottom: '1px dashed #304FFF'
                                                    }}
                                                    title="Final check amount (after withholding tax)"
                                                />
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '4px',
                                                    height: '4px',
                                                    background: 'black',
                                                    marginBottom: '-5px'
                                                }}></span>
                                            </div>

                                            {/* Date */}
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '19.5mm',
                                                right: '66mm',
                                                width: '41mm',
                                                fontSize: '9pt',
                                                textAlign: 'right',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-end',
                                                borderBottom: '1px solid #000',
                                                paddingBottom: '1mm'
                                            }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '4px',
                                                    height: '4px',
                                                    background: 'black',
                                                    marginBottom: '-4px'
                                                }}></span>
                                                <input
                                                    type="text"
                                                    value={format(formData.dueDate, 'dd/MM/yyyy')}
                                                    readOnly
                                                    style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        fontSize: '9pt',
                                                        outline: 'none',
                                                        width: '80px',
                                                        textAlign: 'right',
                                                        cursor: 'default'
                                                    }}
                                                />
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '4px',
                                                    height: '4px',
                                                    background: 'black',
                                                    marginBottom: '-4px'
                                                }}></span>
                                            </div>

                                            {/* Signature */}
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '16mm',
                                                right: '5.5mm',
                                                width: '50mm',
                                                textAlign: 'right'
                                            }}>
                                                {/* Signature Line with Squares */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-end',
                                                    borderBottom: '1px solid #000',
                                                    paddingBottom: '1mm',
                                                    marginBottom: '0mm'
                                                }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '4px',
                                                        height: '4px',
                                                        background: 'black',
                                                        marginBottom: '-4px'
                                                    }}></span>
                                                    {getSignatureData() ? (
                                                        <img
                                                            src={getSignatureData()}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: '40mm',
                                                                maxHeight: '10mm',
                                                                objectFit: 'contain'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '40mm',
                                                            height: '10mm',
                                                            border: '1px dashed #ccc',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '8pt',
                                                            color: '#999'
                                                        }}>
                                                            Signature
                                                        </div>
                                                    )}
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '4px',
                                                        height: '4px',
                                                        background: 'black',
                                                        marginBottom: '-4px'
                                                    }}></span>
                                                </div>

                                                {/* Signature Labels */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    fontSize: '8pt',
                                                    color: '#000',

                                                }}>
                                                    <span style={{ textAlign: 'right' }}>חתימה</span>
                                                    <span style={{ textAlign: 'left', fontSize: '6pt' }}>SIGNATURE</span>
                                                </div>
                                            </div>

                                            {/* Check Number - Bottom Left */}
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '7mm',
                                                left: '35%',
                                                fontSize: '8pt',
                                                color: '#808080',
                                                fontWeight: 'bold'
                                            }}>
                                                {t('CheckNumber')}: {formData.magneticLineData}
                                            </div>

                                            {/* No Changes Allowed */}
                                            {includeNoChangesText && (
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '18mm',
                                                    left: '5.5mm',
                                                    width: '37mm',
                                                    height: '6.5mm',
                                                    border: '1px solid #000',
                                                    fontSize: '9pt',
                                                    fontWeight: 'bold',
                                                    textAlign: 'center',
                                                    lineHeight: '6.5mm'
                                                }}>
                                                    בשיק זה אין לבצע שינוי
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </DialogContent>

                <DialogActions className={nestedStyles.footer} sx={{ direction: direction }}>
                    <button
                        type="button"
                        className={nestedStyles.secondaryBtn}
                        onClick={handleClose}
                    >
                        {t('Close')}
                    </button>
                    <button
                        type="button"
                        className={nestedStyles.primaryBtn}
                        onClick={handlePrint}
                        disabled={loading || !canPrintChecks}
                        dir={direction !== 'ltr' ? 'ltr' : 'rtl'}
                    >
                        {loading ? <CircularProgress size={16} color="inherit" /> : <PrintIcon sx={{ fontSize: 18 }} />}
                        {loading
                            ? t('Printing')
                            : (totalChecksToPrint > 1
                                ? t('PrintChecksCount', { count: totalChecksToPrint })
                                : t('PrintCheck'))}
                    </button>
                </DialogActions>
                </div>
            </Dialog>

            {/* Add Provider Modal */}
            <Dialog
                open={showAddProviderModal}
                onClose={handleCloseAddProviderModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    className: nestedStyles.nestedPaper,
                    elevation: 0,
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(15, 23, 42, 0.45)',
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                        },
                    },
                }}
            >
                <div
                    className={`${nestedStyles.shell} ${filterDirection === 'rtl' ? nestedStyles.rtl : nestedStyles.ltr}`}
                    dir={filterDirection}
                >
                    <div className={nestedStyles.header}>
                        <IconButton onClick={handleCloseAddProviderModal} className={nestedStyles.closeIcon}>
                            <CancelIcon className={nestedStyles.closeIcon} />
                        </IconButton>
                        <div>{t('AddProvider')}</div>
                        <div />
                    </div>

                    <DialogContent
                        className={`${nestedStyles.body} ${filterDirection === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}`}
                        sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <span className={nestedStyles.fieldLabel}>{t('CardNumber')} *</span>
                                <TextField
                                    fullWidth
                                    type="number"
                                    value={cardIdOption}
                                    onChange={(e) => setCardIdOption(parseInt(e.target.value) || 1)}
                                    placeholder={t('EnterCardNumber')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={comboInputProps}
                                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <span className={nestedStyles.fieldLabel}>{t('ProviderName')} *</span>
                                <TextField
                                    fullWidth
                                    value={newProviderData.name}
                                    onChange={(e) => setNewProviderData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder={t('EnterProviderName')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={comboInputProps}
                                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <span className={nestedStyles.fieldLabel}>{t('ProviderCaseId')}</span>
                                <TextField
                                    fullWidth
                                    value={newProviderData.caseId}
                                    onChange={(e) => setNewProviderData(prev => ({ ...prev, caseId: e.target.value }))}
                                    placeholder={t('EnterProviderCaseId')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={comboInputProps}
                                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <span className={nestedStyles.fieldLabel}>{t('ProviderEmail')}</span>
                                <TextField
                                    fullWidth
                                    value={newProviderData.email}
                                    onChange={(e) => setNewProviderData(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder={t('EnterProviderEmail')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={comboInputProps}
                                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <span className={nestedStyles.fieldLabel}>{t('ProviderPhone')}</span>
                                <TextField
                                    fullWidth
                                    value={newProviderData.phoneNumber}
                                    onChange={(e) => setNewProviderData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                    placeholder={t('EnterProviderPhone')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={comboInputProps}
                                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <span className={nestedStyles.fieldLabel}>{t('ProviderLocation')}</span>
                                <TextField
                                    fullWidth
                                    value={newProviderData.location}
                                    onChange={(e) => setNewProviderData(prev => ({ ...prev, location: e.target.value }))}
                                    placeholder={t('EnterProviderLocation')}
                                    variant="standard"
                                    className={fieldStyles.styleInput}
                                    InputProps={comboInputProps}
                                    inputProps={{ style: { textAlign: fieldTextAlign } }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions className={nestedStyles.footer}>
                        <button
                            type="button"
                            className={nestedStyles.secondaryBtn}
                            onClick={handleCloseAddProviderModal}
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            type="button"
                            className={nestedStyles.primaryBtn}
                            onClick={handleAddProvider}
                            disabled={!newProviderData.name.trim()}
                        >
                            {t('Save')}
                        </button>
                    </DialogActions>
                </div>
            </Dialog>

            {/* AddBankModal */}
            <AddBankModal
                open={createBankModal}
                onClose={handleCloseCreateModal}
                selectedBankForCreate={selectedBankForCreate}
                bankOptions={bankOptions}
                handleChangeBankForCreate={handleChangeBankForCreate}
                bankBranch={bankBranch}
                handleChangeCreateBank={handleChangeCreateBank}
                bankAccountNumber={bankAccountNumber}
                cardForNewBank={cardForNewBank}
                processedCardsData={accountCardOptions}
                setCardForNewBank={handleChangeSetCardForNewBank}
                handleAddBank={handleAddBank}
                client={client}
                token={token}
                setLoading={setLoading}
                palCodes={palCodes || []}
                fetchEnums={fetchEnums || (() => { })}
            />

            {/* Print Preview Modal */}
            <Dialog
                open={showPdfModal}
                onClose={handleClosePdfModal}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    className: `${nestedStyles.nestedPaper} ${nestedStyles.nestedPaperSoft}`,
                    elevation: 0,
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <div
                    className={`${nestedStyles.shell} ${filterDirection === 'rtl' ? nestedStyles.rtl : nestedStyles.ltr}`}
                    dir={filterDirection}
                >
                    <div className={nestedStyles.header}>
                        <IconButton onClick={handleClosePdfModal} className={nestedStyles.closeIcon}>
                            <CancelIcon className={nestedStyles.closeIcon} />
                        </IconButton>
                        <div className={nestedStyles.headerTitle}>
                            <PreviewIcon sx={{ fontSize: 22 }} />
                            <span>
                                {pdfUrl
                                    ? (printedChecksPreview.length > 1 ? t('PrintChecks') : t('CheckPreview'))
                                    : t('ReviewChecksBeforePrint')}
                            </span>
                            {printedChecksPreview.length > 0 && (
                                <span className={nestedStyles.headerMeta}>
                                    {pdfUrl
                                        ? t('ChecksPreviewCount', { count: printedChecksPreview.length })
                                        : t('ReviewChecksBeforePrintHint', { count: printedChecksPreview.length })}
                                </span>
                            )}
                            {printedChecksPreview.length > 1 && (
                                <Chip
                                    size="small"
                                    label={printedChecksPreview.length}
                                    sx={{
                                        height: 20,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        bgcolor: '#304FFF',
                                        color: 'white',
                                    }}
                                />
                            )}
                        </div>
                        <div />
                    </div>

                    <DialogContent
                        className={nestedStyles.body}
                        sx={{
                            p: '0 !important',
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflowY: 'auto',
                            bgcolor: '#eef1f8',
                            ...scrollbarSx,
                        }}
                    >
                        {printedChecksPreview.length > 0 ? (
                            <Box sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.25,
                            }}>
                                {printedChecksPreview.map((item, index) => (
                                    <Box key={`${item.checkNumber}-${index}`} sx={{ width: '100%', maxWidth: 480 }}>
                                        <PrintedCheckPreviewCard
                                            item={item}
                                            index={index}
                                            t={t}
                                            onClick={() => setCheckDetailView({ item, index })}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        ) : pdfUrl ? (
                            <Box sx={{ height: 0, display: 'none' }} aria-hidden="true" />
                        ) : null}
                    </DialogContent>

                    <DialogActions className={nestedStyles.footer} sx={{ direction: filterDirection }}>
                        <button
                            type="button"
                            className={nestedStyles.secondaryBtn}
                            onClick={handleClosePdfModal}
                        >
                            {t('Close')}
                        </button>
                        <button
                            type="button"
                            className={nestedStyles.primaryBtn}
                            onClick={() => (pdfUrl ? handlePrintFromPreview() : confirmPrint())}
                            disabled={loading || printedChecksPreview.length === 0}
                        >
                            {loading ? <CircularProgress size={16} color="inherit" /> : <PrintIcon sx={{ fontSize: 18 }} />}
                            {loading
                                ? t('Printing')
                                : (printedChecksPreview.length > 1
                                    ? t('PrintChecksCount', { count: printedChecksPreview.length })
                                    : t('PrintCheck'))}
                        </button>
                    </DialogActions>
                </div>
            </Dialog>

            {/* Single check detail modal */}
            <Dialog
                open={!!checkDetailView}
                onClose={() => setCheckDetailView(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    className: `${nestedStyles.nestedPaper} ${nestedStyles.nestedPaperSoft}`,
                    elevation: 0,
                }}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <div
                    className={`${nestedStyles.shell} ${filterDirection === 'rtl' ? nestedStyles.rtl : nestedStyles.ltr}`}
                    dir={filterDirection}
                >
                    <div className={nestedStyles.header}>
                        <IconButton onClick={() => setCheckDetailView(null)} className={nestedStyles.closeIcon}>
                            <CancelIcon className={nestedStyles.closeIcon} />
                        </IconButton>
                        <div className={nestedStyles.headerTitle}>
                            <span>
                                {t('ViewCheckDetails')}
                                {checkDetailView ? ` — ${t('CheckNumber')} ${checkDetailView.item.checkNumber}` : ''}
                            </span>
                        </div>
                        <div />
                    </div>
                    <DialogContent
                        className={nestedStyles.body}
                        sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', bgcolor: '#f4f6fb', ...scrollbarSx }}
                    >
                        {checkDetailView && (
                            <PrintedCheckDetailView
                                key={checkDetailView.item.checkNumber}
                                item={checkDetailView.item}
                                index={checkDetailView.index}
                                signatureSrc={previewPrintSettings.signatureSrc}
                                includeNoChangesText={previewPrintSettings.includeNoChangesText}
                                t={t}
                            />
                        )}
                    </DialogContent>
                    <DialogActions className={nestedStyles.footer} sx={{ direction: filterDirection }}>
                        <button
                            type="button"
                            className={nestedStyles.primaryBtn}
                            onClick={() => setCheckDetailView(null)}
                        >
                            {t('Close')}
                        </button>
                    </DialogActions>
                </div>
            </Dialog>

            {/* Signature Modal */}
            <Dialog
                open={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    className: `${nestedStyles.nestedPaper} ${nestedStyles.nestedPaperSoft}`,
                    elevation: 0,
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <div
                    className={`${nestedStyles.shell} ${filterDirection === 'rtl' ? nestedStyles.rtl : nestedStyles.ltr}`}
                    dir={filterDirection}
                >
                    <div className={nestedStyles.header}>
                        <IconButton onClick={() => setShowSignatureModal(false)} className={nestedStyles.closeIcon}>
                            <CancelIcon className={nestedStyles.closeIcon} />
                        </IconButton>
                        <div className={nestedStyles.headerTitle}>
                            <span>{t('CreateSignature')}</span>
                        </div>
                        <div />
                    </div>

                    <DialogContent
                        className={nestedStyles.body}
                        sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', bgcolor: '#f4f6fb', ...scrollbarSx }}
                    >
                        <Box
                            className={nestedStyles.sectionCard}
                            sx={{
                                p: 2,
                                textAlign: 'center',
                                borderStyle: 'dashed',
                            }}
                        >
                            <SignaturePad
                                ref={sigPad}
                                canvasProps={{
                                    className: 'signatureCanvasContainer',
                                    style: {
                                        width: '100%',
                                        height: '200px',
                                        border: '1px solid #e5e7f6',
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                    },
                                }}
                                onEnd={handleSignatureChange}
                            />
                        </Box>
                    </DialogContent>

                    <DialogActions className={nestedStyles.footer} sx={{ direction: filterDirection }}>
                        <button
                            type="button"
                            className={nestedStyles.secondaryBtn}
                            onClick={clearSignature}
                        >
                            {t('Clear')}
                        </button>
                        <button
                            type="button"
                            className={nestedStyles.primaryBtn}
                            onClick={saveSignature}
                        >
                            {t('Save')}
                        </button>
                    </DialogActions>
                </div>
            </Dialog>
        </>
    );
};

export default PrintChecksModal;
